import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  alpha,
  useTheme,
  Stack,
} from "@mui/material";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import { useUserContext } from "../Contexts/UserContext";
import { prepareAssignedVessel } from "./login/prepareAssignedVessel";
import { listProvisionableVessels } from "../services/login/provisionUsersForVessel";

const getProvisioningWarningMessage = (warnings, t) => {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return "";
  }

  const missingUsersWarning = warnings.find(
    (warning) => warning.code === "missing-users"
  );
  if (missingUsersWarning) {
    return t("warning_missing_user_refs", {
      count: missingUsersWarning.count,
    });
  }

  const noActiveAccessWarning = warnings.find(
    (warning) => warning.code === "no-active-access"
  );
  if (noActiveAccessWarning) {
    return t("warning_no_active_vessel_users");
  }

  const missingParticularsWarning = warnings.find(
    (warning) => warning.code === "missing-vessel-particulars"
  );
  if (missingParticularsWarning) {
    return t("warning_missing_vessel_particulars");
  }

  return "";
};

const LoginSettingsModal = ({ open, onClose, onShipSelected, t, ship }) => {
  const theme = useTheme();
  const { setUsersData } = useUserContext();

  const [availableShips, setAvailableShips] = useState([]);
  const [loadingShips, setLoadingShips] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [selectedShipId, setSelectedShipId] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const selectedShip = useMemo(
    () => availableShips.find((candidate) => candidate.id === selectedShipId) || null,
    [availableShips, selectedShipId]
  );

  const fetchShips = useCallback(async () => {
    setLoadingShips(true);
    try {
      const shipList = await listProvisionableVessels();
      setAvailableShips(shipList);
    } catch (error) {
      console.error("Error fetching ships:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(t("error_fetch_ships"));
      setSnackbarOpen(true);
    } finally {
      setLoadingShips(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) return;

    fetchShips();
    setSelectedShipId(ship?.id || ship?.shipId || "");
  }, [fetchShips, open, ship]);

  const handleUseVessel = async () => {
    if (!selectedShipId) return;

    setIsPreparing(true);

    try {
      const { shipData, userList, warnings } = await prepareAssignedVessel(
        selectedShipId
      );
      setUsersData(userList);
      await Promise.resolve(onShipSelected(shipData));
      const warningMessage = getProvisioningWarningMessage(warnings, t);
      if (warningMessage) {
        setSnackbarSeverity("warning");
        setSnackbarMessage(warningMessage);
        setSnackbarOpen(true);
      }
      onClose();
    } catch (error) {
      console.error("Direct sync failed:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(t("error_sync_failed"));
      setSnackbarOpen(true);
    } finally {
      setIsPreparing(false);
    }
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={isPreparing ? undefined : onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, fontSize: "1.1rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <DirectionsBoatIcon color="primary" />
            <Typography variant="inherit">{t("select_vessel")}</Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 3 }}>
          {loadingShips ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={32} />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {t("loading_fleet_data")}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("vessel")}
              </Typography>

              <FormControl fullWidth>
                <Select
                  value={selectedShipId}
                  onChange={(e) => setSelectedShipId(e.target.value)}
                  displayEmpty
                  disabled={isPreparing}
                  sx={{
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    fontWeight: 600,
                  }}
                >
                  {availableShips.map((candidate) => (
                    <MenuItem key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isPreparing && selectedShip && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 0.5,
                    color: "text.secondary",
                  }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    {t("preparing_vessel", { vessel: selectedShip.name })}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Button
            onClick={onClose}
            disabled={isPreparing}
            variant="text"
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleUseVessel}
            disabled={!selectedShipId || loadingShips || isPreparing}
            variant="contained"
            sx={{ borderRadius: 2, px: 2.5, fontWeight: 700 }}
          >
            {t("use_this_vessel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoginSettingsModal;
